// Platform console -> AI providers. G3 of docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md
// — the shared platform-level credentials every tenant's AI calls fall back
// to (backend/utils/aiCredentials.js's "shared" tier). Mirrors
// features/admin/sms-gateway's mask-on-read/encrypt-on-save/"save & test"
// pattern, the one credential-screen convention this codebase already has.
import { useEffect, useState } from 'react';
import { platformApi } from '../lib/platformApi/client';

interface ProviderRow {
  provider: 'anthropic' | 'openai' | 'gemini' | 'sarvam';
  enabled: boolean;
  default_model: string | null;
  api_key_set: boolean;
  api_key_hint: string | null;
  api_key: string;
  admin_key_set: boolean;
  admin_key_hint: string | null;
  admin_key: string;
  last_test_at: string | null;
  last_test_ok: boolean | null;
  last_test_message: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

const LABEL: Record<ProviderRow['provider'], string> = {
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  sarvam: 'Sarvam AI (voice)',
};

const DEFAULT_MODEL_HINT: Record<ProviderRow['provider'], string> = {
  anthropic: 'e.g. claude-sonnet-4-5',
  openai: 'e.g. gpt-4o',
  gemini: 'e.g. gemini-2.0-flash',
  sarvam: 'n/a — voice vendor, model fixed per capability',
};

interface EditState { api_key: string; default_model: string; enabled: boolean }

interface AiProvidersScreenProps {
  onBack: () => void;
}

export default function AiProvidersScreen({ onBack }: AiProvidersScreenProps) {
  const [rows, setRows] = useState<ProviderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { ok: boolean; message: string }>>({});

  const refresh = () => {
    setLoading(true);
    platformApi.get<{ providers: ProviderRow[] }>('/ai/providers')
      .then(({ providers }) => {
        setRows(providers);
        const next: Record<string, EditState> = {};
        for (const p of providers) next[p.provider] = { api_key: p.api_key, default_model: p.default_model || '', enabled: p.enabled };
        setEdits(next);
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const save = async (provider: string) => {
    setBusy(provider);
    setError('');
    try {
      const e = edits[provider] ?? { api_key: '', default_model: '', enabled: true };
      await platformApi.patch(`/ai/providers/${provider}`, {
        api_key: e.api_key,
        default_model: e.default_model || null,
        enabled: e.enabled,
      });
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const saveAndTest = async (provider: string) => {
    setBusy(provider);
    setError('');
    try {
      const e = edits[provider] ?? { api_key: '', default_model: '', enabled: true };
      await platformApi.patch(`/ai/providers/${provider}`, {
        api_key: e.api_key,
        default_model: e.default_model || null,
        enabled: e.enabled,
      });
      const result = await platformApi.post<{ ok: boolean; message: string }>(`/ai/providers/${provider}/test`, {});
      setTestResult(prev => ({ ...prev, [provider]: result }));
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>AI providers</div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            Shared platform-level credentials. Every tenant's AI calls fall back to these unless a
            tenant has its own dedicated account (not configured in this deployment).
          </div>
        </div>
        <button onClick={onBack} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>← Back to tenants</button>
      </div>

      {error && (
        <div style={{ padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {loading && <div style={{ color: '#9CA3AF', fontSize: 13 }}>Loading…</div>}

      {!loading && rows.map(row => {
        const edit = edits[row.provider] || { api_key: '', default_model: '', enabled: true };
        const tr = testResult[row.provider];
        return (
          <div key={row.provider} className="card" style={{ padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{LABEL[row.provider]}</div>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={edit.enabled}
                  onChange={ev => setEdits(prev => ({ ...prev, [row.provider]: { ...edit, enabled: ev.target.checked } }))}
                />
                Enabled
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                  API key {row.api_key_set && row.api_key_hint ? `(saved, ${row.api_key_hint})` : row.api_key_set ? '(saved)' : '(not set)'}
                </div>
                <input
                  type="password"
                  value={edit.api_key}
                  onChange={ev => setEdits(prev => ({ ...prev, [row.provider]: { ...edit, api_key: ev.target.value } }))}
                  onFocus={ev => { if (ev.target.value === row.api_key && row.api_key_set) ev.target.select(); }}
                  placeholder={row.api_key_set ? 'Leave as-is to keep the saved key' : 'Paste API key'}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>Default model override</div>
                <input
                  type="text"
                  value={edit.default_model}
                  onChange={ev => setEdits(prev => ({ ...prev, [row.provider]: { ...edit, default_model: ev.target.value } }))}
                  placeholder={DEFAULT_MODEL_HINT[row.provider]}
                />
              </div>
            </div>

            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10 }}>
              {row.last_test_at
                ? `Last tested ${new Date(row.last_test_at).toLocaleString()} — ${row.last_test_ok ? '✓ OK' : '✗ ' + (row.last_test_message || 'failed')}`
                : 'Never tested.'}
              {row.updated_at && <> · Last saved {new Date(row.updated_at).toLocaleString()} by {row.updated_by || 'unknown'}</>}
            </div>

            {tr && (
              <div style={{
                padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 10,
                background: tr.ok ? '#DCFCE7' : '#FEE2E2', color: tr.ok ? '#166534' : '#991B1B',
              }}>
                {tr.ok ? '✓ ' : '✗ '}{tr.message}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" disabled={busy === row.provider} onClick={() => save(row.provider)}>
                {busy === row.provider ? 'Saving…' : 'Save'}
              </button>
              <button disabled={busy === row.provider} onClick={() => saveAndTest(row.provider)}
                style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
                {busy === row.provider ? 'Working…' : 'Save & test connection'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
